import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createOverlayPipEffect,
  EffectPreset,
  EffectRow,
  EffectType,
  initEffectUpload,
  listEffects,
  uploadToPresignedUrl,
} from "../api";

// Single source of truth for effect-type registry. Add a row + a form case
// below as each new effect type ships.
const EFFECT_TYPES: {
  id: EffectType;
  label: string;
  description: string;
}[] = [
  {
    id: "overlay-pip",
    label: "Overlay PIP",
    description:
      "Une miniature glisse dans la vidéo principale pendant N secondes. Audio overlay mute, ta voix continue.",
  },
];

const PRESETS: { id: EffectPreset; label: string }[] = [
  { id: "bottom-left", label: "Bas gauche" },
  { id: "bottom-center", label: "Bas centré (défaut)" },
  { id: "bottom-right", label: "Bas droite" },
];

interface CatalogOption {
  key: string;
  uses: number;
  lastUsed: string;
}

type PickerMode = "catalog" | "upload" | "manual";

interface VideoPickerProps {
  label: string;
  value: string;
  onChange: (key: string) => void;
  catalogOptions: CatalogOption[];
}

function VideoPicker({
  label,
  value,
  onChange,
  catalogOptions,
}: VideoPickerProps) {
  const [mode, setMode] = useState<PickerMode>(
    catalogOptions.length > 0 ? "catalog" : "upload"
  );
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      setError(null);
      setProgress(0);
      try {
        const init = await initEffectUpload(
          file.name,
          file.type || "video/mp4"
        );
        await uploadToPresignedUrl(
          init.uploadUrl,
          file,
          file.type || "video/mp4",
          setProgress
        );
        onChange(init.s3Key);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Upload échoué");
      } finally {
        setUploading(false);
      }
    },
    [onChange]
  );

  const tabs: { id: PickerMode; label: string; disabled?: boolean }[] = [
    {
      id: "catalog",
      label: "Catalogue",
      disabled: catalogOptions.length === 0,
    },
    { id: "upload", label: "Uploader" },
    { id: "manual", label: "Coller S3 key" },
  ];

  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-charcoal">{label}</label>

      <div className="flex gap-1 text-xs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setMode(t.id)}
            disabled={t.disabled}
            className={`rounded px-2 py-1 transition ${
              mode === t.id
                ? "bg-charcoal text-ivory"
                : "text-concrete hover:text-charcoal disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-concrete"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {mode === "catalog" && (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-md border border-stone bg-white px-3 py-2 font-mono text-xs text-charcoal focus:border-charcoal focus:outline-none"
        >
          <option value="">— choisir une vidéo déjà utilisée —</option>
          {catalogOptions.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.key.replace(/^effects-inputs\//, "")} ({opt.uses}x · dernière{" "}
              {new Date(opt.lastUsed).toLocaleDateString("fr-FR")})
            </option>
          ))}
        </select>
      )}

      {mode === "upload" && (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="whitespace-nowrap rounded-md bg-charcoal px-3 py-2 text-sm font-medium text-ivory hover:bg-black disabled:opacity-50"
          >
            {uploading ? `Upload ${progress}%` : "Choisir un fichier"}
          </button>
          {value && (
            <span className="flex-1 self-center truncate font-mono text-xs text-concrete">
              {value}
            </span>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
          />
        </div>
      )}

      {mode === "manual" && (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="effects-inputs/<id>.mp4 ou uploads/<id>.webm"
          className="w-full rounded-md border border-stone bg-white px-3 py-2 font-mono text-xs text-charcoal focus:border-charcoal focus:outline-none"
        />
      )}

      {error && <div className="text-xs text-red-600">{error}</div>}
    </div>
  );
}

interface OverlayPipFormProps {
  catalogOptions: CatalogOption[];
  onComposed: () => void;
}

function OverlayPipForm({ catalogOptions, onComposed }: OverlayPipFormProps) {
  const [mainKey, setMainKey] = useState("");
  const [overlayKey, setOverlayKey] = useState("");
  const [preset, setPreset] = useState<EffectPreset>("bottom-center");
  const [startTime, setStartTime] = useState(3);
  const [duration, setDuration] = useState(12);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);

  const submit = async () => {
    if (!mainKey.trim() || !overlayKey.trim()) {
      setSubmitError("Renseigne les 2 vidéos");
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    setSubmitOk(false);
    try {
      await createOverlayPipEffect({
        mainVideoKey: mainKey.trim(),
        overlayVideoKey: overlayKey.trim(),
        preset,
        startTime,
        duration,
      });
      setMainKey("");
      setOverlayKey("");
      setSubmitOk(true);
      onComposed();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : "Échec du compose");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-4 space-y-4">
      <VideoPicker
        label="Vidéo principale (où tu parles)"
        value={mainKey}
        onChange={setMainKey}
        catalogOptions={catalogOptions}
      />
      <VideoPicker
        label="Vidéo overlay (miniature)"
        value={overlayKey}
        onChange={setOverlayKey}
        catalogOptions={catalogOptions}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Position
          </label>
          <div className="space-y-1">
            {PRESETS.map((p) => (
              <label key={p.id} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="preset"
                  checked={preset === p.id}
                  onChange={() => setPreset(p.id)}
                />
                {p.label}
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Début (s)
          </label>
          <input
            type="number"
            value={startTime}
            onChange={(e) =>
              setStartTime(Math.max(0, parseFloat(e.target.value) || 0))
            }
            min={0}
            step={0.5}
            className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-charcoal">
            Durée visible (s)
          </label>
          <input
            type="number"
            value={duration}
            onChange={(e) =>
              setDuration(Math.max(2, parseFloat(e.target.value) || 0))
            }
            min={2}
            step={0.5}
            className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
          />
        </div>
      </div>

      {submitError && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {submitError}
        </div>
      )}
      {submitOk && (
        <div className="rounded-md bg-lemonade/20 px-3 py-2 text-sm text-charcoal">
          Effet en file. Va dans l&apos;onglet « Effets effectués » pour suivre
          son avancement.
        </div>
      )}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={submitting}
        className="rounded-md bg-charcoal px-4 py-2 text-sm font-semibold text-ivory hover:bg-black disabled:opacity-50"
      >
        {submitting ? "Envoi…" : "Composer"}
      </button>
    </div>
  );
}

export default function AdminEffects() {
  const [effectType, setEffectType] = useState<EffectType>("overlay-pip");
  const [pastEffects, setPastEffects] = useState<EffectRow[]>([]);

  const loadPast = useCallback(async () => {
    try {
      const rows = await listEffects();
      setPastEffects(rows);
    } catch {
      setPastEffects([]);
    }
  }, []);

  useEffect(() => {
    void loadPast();
  }, [loadPast]);

  // Dedupe input keys across past effects of the same type, keeping the
  // most recent createdAt as lastUsed.
  const catalogOptions = useMemo<CatalogOption[]>(() => {
    const map = new Map<string, { uses: number; lastUsed: string }>();
    for (const e of pastEffects) {
      if (e.effectType !== effectType) continue;
      for (const k of [e.params.mainVideoKey, e.params.overlayVideoKey]) {
        if (!k) continue;
        const prev = map.get(k);
        if (prev) {
          map.set(k, {
            uses: prev.uses + 1,
            lastUsed:
              prev.lastUsed > e.createdAt ? prev.lastUsed : e.createdAt,
          });
        } else {
          map.set(k, { uses: 1, lastUsed: e.createdAt });
        }
      }
    }
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.lastUsed.localeCompare(a.lastUsed));
  }, [pastEffects, effectType]);

  const currentDef = EFFECT_TYPES.find((t) => t.id === effectType)!;

  return (
    <section className="rounded-lg border border-stone bg-white p-4 md:p-6">
      <h2 className="text-lg font-bold text-charcoal">Nouvel effet</h2>

      <div className="mt-4 max-w-md">
        <label className="mb-2 block text-sm font-medium text-charcoal">
          Type d&apos;effet
        </label>
        <select
          value={effectType}
          onChange={(e) => setEffectType(e.target.value as EffectType)}
          className="w-full rounded-md border border-stone bg-white px-3 py-2 text-sm"
        >
          {EFFECT_TYPES.map((t) => (
            <option key={t.id} value={t.id}>
              {t.label}
            </option>
          ))}
        </select>
        <p className="mt-2 text-sm text-concrete">{currentDef.description}</p>
      </div>

      {effectType === "overlay-pip" && (
        <OverlayPipForm
          catalogOptions={catalogOptions}
          onComposed={() => void loadPast()}
        />
      )}
    </section>
  );
}
